import {
  CameraIcon,
  ChatAlt2Icon,
  FilmIcon,
  MicrophoneIcon,
  MusicNoteIcon,
  PencilAltIcon,
  SparklesIcon,
  SpeakerphoneIcon,
  SwitchHorizontalIcon,
  TranslateIcon,
  UserCircleIcon,
  UserGroupIcon,
  VideoCameraIcon,
  ViewGridAddIcon,
} from '@heroicons/react/outline';
import { VolumeUpIcon } from '@heroicons/react/solid';
import { FaPhotoVideo } from 'react-icons/fa';
import { FaImages, FaRobot } from 'react-icons/fa6';
import { GiSofa, GiSoundWaves } from 'react-icons/gi';
import { RiRecordCircleFill } from 'react-icons/ri';

/**
 * Single source of truth for the tool taxonomy used across the app
 * (the Tools mega-flyout in the sidebar and the per-page ToolsNav).
 *
 * Keep `badge` as a small enum rather than JSX so this stays a plain data
 * module — consumers render it via the shared <NavBadge /> component.
 */

export type ToolBadge = 'new' | 'beta';

export interface ToolItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Stable identifier, also used for favorites/pinning. */
  slug: string;
  badge?: ToolBadge;
}

export interface ToolCategory {
  /** Display label, e.g. "Images". Passed through i18n at render time. */
  title: string;
  /** Stable identifier, e.g. "images". */
  slug: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Pastel gradient classes used by the Tools mega-flyout cards. */
  color: string;
  /**
   * A cross-listing hub (e.g. "AI Agents"): its tools may ALSO live in a
   * topical category, and that topical category stays the tool's home for
   * active-state purposes (ToolsNav pill, `findToolBySlug`, ...). Tools that
   * appear only in the hub resolve to the hub.
   */
  hub?: boolean;
  tools: ToolItem[];
}

/**
 * Tools listed both in the "AI Agents" hub AND in their topical column are
 * declared once here so the two entries can never drift apart. Same slug in
 * both places means one favorite/pin, one active state.
 */
const videoAgentTool: ToolItem = {
  name: 'Video Agent',
  href: '/create-video',
  icon: SparklesIcon,
  slug: 'video-agent',
  badge: 'new',
};

export const toolCategories: ToolCategory[] = [
  {
    // Every agent-style product in one place, first. Video Agent is also
    // listed under Videos (its topical home) — see `videoAgentTool`.
    title: 'AI Agents',
    slug: 'agents',
    icon: SparklesIcon,
    color:
      'from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20',
    hub: true,
    tools: [
      {
        name: 'Marky Agent',
        href: '/chat',
        icon: ChatAlt2Icon,
        slug: 'chat',
      },
      {
        name: 'AI Employees',
        href: '/employees',
        icon: UserGroupIcon,
        slug: 'employees',
        badge: 'new',
      },
      videoAgentTool,
      {
        name: 'Bots',
        href: '/agents',
        icon: FaRobot,
        slug: 'bots',
      },
      {
        name: 'Workflows',
        href: '/workflows',
        icon: ViewGridAddIcon,
        slug: 'workflows',
      },
      {
        name: 'Brand Voice',
        href: '/voices',
        icon: SpeakerphoneIcon,
        slug: 'voices',
      },
    ],
  },
  {
    title: 'Images',
    slug: 'images',
    icon: FaImages,
    color: 'from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20',
    tools: [
      {
        name: 'Image Generator',
        href: '/ai-images',
        icon: FaImages,
        slug: 'ai-images',
      },
      {
        name: 'Image Editor',
        href: '/ai-image-editor',
        icon: PencilAltIcon,
        slug: 'ai-image-editor',
      },
      {
        name: 'Magic Upscaler',
        href: '/magic-upscaler',
        icon: FaImages,
        slug: 'magic-upscaler',
      },
      {
        name: 'Headshots',
        href: '/headshots',
        icon: CameraIcon,
        slug: 'headshots',
      },
      {
        name: 'AI Photo Studio',
        href: '/ai-images/models',
        icon: FaPhotoVideo,
        slug: 'ai-images-models',
      },
      {
        name: 'Interior Designer',
        href: '/ai-interior-designer',
        icon: GiSofa,
        slug: 'ai-interior-designer',
      },
      {
        name: 'Remove Background',
        href: '/ai-images?action=remove-background',
        icon: FaImages,
        slug: 'remove-background',
      },
      {
        name: 'Replace Background',
        href: '/ai-images?action=replace-background',
        icon: FaImages,
        slug: 'replace-background',
      },
    ],
  },
  {
    title: 'Videos',
    slug: 'videos',
    icon: VideoCameraIcon,
    color:
      'from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20',
    tools: [
      videoAgentTool,
      {
        name: 'Video Generator',
        href: '/ai-videos?tab=create-video',
        icon: VideoCameraIcon,
        slug: 'ai-videos',
      },
      {
        name: 'Motion Control',
        href: '/ai-videos?tab=motion-control',
        icon: VideoCameraIcon,
        slug: 'motion-control',
      },
      {
        name: 'Extend Video',
        href: '/ai-videos?tab=extend-video',
        icon: FilmIcon,
        slug: 'extend-video',
        badge: 'new',
      },
      {
        name: 'Edit Video',
        href: '/ai-videos?tab=edit-video',
        icon: FilmIcon,
        slug: 'edit-video',
        badge: 'new',
      },
      {
        name: 'Upscale Video',
        href: '/ai-videos?tab=upscale-video',
        icon: SparklesIcon,
        slug: 'upscale-video',
        badge: 'new',
      },
      {
        name: 'Talking Videos',
        href: '/ai-talking-videos',
        icon: UserCircleIcon,
        slug: 'ai-talking-videos',
      },
      {
        name: 'Video Editor',
        href: '/ai-video-editor',
        icon: FilmIcon,
        slug: 'ai-video-editor',
        badge: 'beta',
      },
      // seedance 2.5
      {
        name: 'Seedance 2.5',
        href: '/ai-videos?model=seedance-2.5',
        icon: SparklesIcon,
        slug: 'seedance-2.5',
        badge: 'new',
      },
      {
        name: 'MiniMax H3',
        href: '/ai-videos?model=minimax-h3',
        icon: SparklesIcon,
        slug: 'minimax-h3',
        badge: 'new',
      },
      {
        name: 'WAN 3.0',
        href: '/ai-videos?model=wan-3.0',
        icon: SparklesIcon,
        slug: 'wan-3.0',
        badge: 'new',
      },
      {
        name: 'Gemini Omni Flash 1.1',
        href: '/ai-videos?model=gemini-omni-flash-1.1',
        icon: SparklesIcon,
        slug: 'gemini-omni-flash-1.1',
        badge: 'new',
      },
    ],
  },
  {
    title: 'Audio',
    slug: 'audio',
    icon: VolumeUpIcon,
    color:
      'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
    tools: [
      {
        name: 'Music Generator',
        href: '/music-generator',
        icon: MusicNoteIcon,
        slug: 'music-generator',
      },
      {
        name: 'Text to Speech',
        href: '/tts',
        icon: MicrophoneIcon,
        slug: 'tts',
      },
      {
        name: 'Voice Changer',
        href: '/voice-changer',
        icon: SwitchHorizontalIcon,
        slug: 'voice-changer',
        badge: 'new',
      },
      {
        name: 'AI Dubbing',
        href: '/dubbing',
        icon: TranslateIcon,
        slug: 'dubbing',
      },
      {
        name: 'Speech to Text',
        href: '/audios',
        icon: GiSoundWaves,
        slug: 'audios',
      },
      {
        name: 'AI Audio Generator',
        href: '/audio-generator',
        icon: VolumeUpIcon,
        slug: 'audio-generator',
        badge: 'new',
      },
      {
        name: 'Sound Effect Generator',
        href: '/sound-effects',
        icon: VolumeUpIcon,
        slug: 'sound-effects',
      },
      {
        name: 'Record & Transcribe',
        href: '/note-taker',
        icon: RiRecordCircleFill,
        slug: 'note-taker',
      },
    ],
  },
];

const basePath = (href: string) => href.split('?')[0].split('#')[0];

export interface ActiveTool {
  tool: ToolItem;
  category: ToolCategory;
}

/**
 * Categories in lookup order: topical categories first, cross-listing hubs
 * last, so a tool listed in both resolves to its topical home.
 */
const lookupOrder = (): ToolCategory[] =>
  [...toolCategories].sort((a, b) => Number(!!a.hub) - Number(!!b.hub));

/** Look up a tool (and its category) by its slug. */
export function findToolBySlug(slug?: string): ActiveTool | undefined {
  if (!slug) return undefined;
  for (const category of lookupOrder()) {
    const tool = category.tools.find((t) => t.slug === slug);
    if (tool) return { tool, category };
  }
  return undefined;
}

/**
 * Derive the active tool from a Next.js `asPath`. Prefers an exact match
 * (including query string, so action/tab-specific tools win) and falls back
 * to the first tool whose base path matches (e.g. /ai-videos -> Video Generator).
 */
export function findActiveByPath(asPath?: string): ActiveTool | undefined {
  if (!asPath) return undefined;
  const path = basePath(asPath);
  let fallback: ActiveTool | undefined;

  for (const category of lookupOrder()) {
    for (const tool of category.tools) {
      if (tool.href === asPath) return { tool, category };
      if (basePath(tool.href) === path && !fallback) {
        fallback = { tool, category };
      }
    }
  }
  return fallback;
}
